import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Pill, Users, Activity, ShieldCheck, Languages, Syringe, PhoneCall } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import { StoryCarousel } from "@/components/StoryCarousel";
import useSEO from "@/hooks/useSEO";

const HERO = "https://static.prod-images.emergentagent.com/jobs/e1910145-6c8d-4199-9278-87a3c9131c27/images/5b52a4569b171f2bbbc4b40be2da63bceb7e5a30ffa4af9c6ee21b3938593f76.jpeg";
const SITE = "https://manasneuropsychiatryhospital.in";

const pillars = [
  { icon: Pill, title: "તબીબી ડિટોક્સ (વ્યસન છોડાવવાની સારવાર)", desc: "દારૂ કે ડ્રગ્સ છોડતી વખતે આવતી તકલીફો — ધ્રુજારી, ઊંઘ ન આવવી, ખેંચ, ભ્રમ — ડૉક્ટરની ૨૪×૭ દેખરેખ હેઠળ સુરક્ષિત રીતે સંભાળવામાં આવે છે." },
  { icon: Activity, title: "વર્તન ઉપચાર અને કાઉન્સેલિંગ", desc: "CBT, પ્રેરણાત્મક કાઉન્સેલિંગ અને ગ્રુપ થેરાપી રોજ — કારણ કે ફક્ત દવાથી વ્યસન છૂટતું નથી, વિચાર બદલવા પડે છે." },
  { icon: Users, title: "પરિવારની સાથે સારવાર", desc: "પહેલા અઠવાડિયાથી જ પરિવારને સામેલ કરીએ છીએ. વ્યસનમુક્તિ એકલા દર્દીની લડાઈ નથી, આખા ઘરની છે." },
  { icon: ShieldCheck, title: "ફરી ન થાય તેની કાળજી (રીલેપ્સ પ્રિવેન્શન)", desc: "ડિસ્ચાર્જ પછીની યોજના, નિયમિત ફોલો-અપ, વ્યસનમુક્ત મિત્રવર્તુળ અને જરૂર પડે તો નાલ્ટ્રેક્સોન ઈમ્પ્લાન્ટ." },
];

const treats = [
  "દારૂ (આલ્કોહોલ)",
  "તમાકુ, મસાલો, મવો, બીડી",
  "ગાંજો અને ચરસ",
  "અફીણ, ડોડા, બ્રાઉન સુગર",
  "આલ્પ્રાઝોલમ અને ઊંઘની ગોળીઓ",
  "કોકેઈન અને પેથીડીન",
  "મોબાઈલ, ગેમિંગ અને પોર્નનું વ્યસન",
  "જુગાર અને સટ્ટાનું વ્યસન",
];

const steps = [
  { t: "અઠવાડિયું ૧ — સ્થિર કરવું", d: "સંપૂર્ણ તબીબી તપાસ, છોડતી વખતની તકલીફોની સારવાર, ઊંઘ અને પોષણ સુધારવું." },
  { t: "અઠવાડિયું ૨–૪ — સમજવું", d: "વ્યક્તિગત થેરાપીથી શોધીએ છીએ કે વ્યસન કેમ શરૂ થયું, અને રોગ વિશે દર્દી–પરિવારને શિક્ષણ." },
  { t: "અઠવાડિયું ૫–૮ — ફરી ઊભા થવું", d: "પરિવાર સાથેની બેઠકો, કામ–ધંધાની યોજના અને ગ્રુપ થેરાપી મુખ્ય બને છે." },
  { t: "અઠવાડિયું ૯–૧૨ — ટકાવવું", d: "ડિસ્ચાર્જ પછીની યોજના, વ્યસનમુક્ત સમૂહમાં જોડાણ અને દવાની સમીક્ષા." },
];

const faqs = [
  { q: "દારૂ કે ડ્રગ્સ છોડાવવા દાખલ થવું જરૂરી છે?", a: "હંમેશાં નહીં. ઘણા દર્દીઓની સારવાર OPDમાં જ થાય છે — દવા, કાઉન્સેલિંગ અને નિયમિત ફોલો-અપથી. જ્યાં ઘરે વ્યસન સહેલાઈથી મળી રહે, અગાઉ ખેંચ કે ભ્રમ આવ્યાં હોય, અથવા વારંવાર પ્રયાસ નિષ્ફળ ગયા હોય, ત્યાં ૧૦થી ૧૫ દિવસ દાખલ થવું વધુ સુરક્ષિત છે." },
  { q: "દર્દી સારવાર માટે તૈયાર ન હોય તો શું?", a: "આ સૌથી સામાન્ય પ્રશ્ન છે. પરિવાર એકલા આવીને પણ પરામર્શ કરી શકે છે — અમે તમને સમજાવીએ છીએ કે દર્દી સાથે કેવી રીતે વાત કરવી, શું ટાળવું, અને તૈયારી ઊભી કરવા શું કરવું. દબાણ કરતાં આ રીત વધુ કામ કરે છે." },
  { q: "દારૂ અચાનક છોડી દેવું જોખમી છે?", a: "હા. લાંબા સમયથી નિયમિત દારૂ પીતા દર્દીમાં અચાનક બંધ કરવાથી ખેંચ (fits) અને 'ડિલિરિયમ ટ્રીમેન્સ' જેવી જીવલેણ સ્થિતિ થઈ શકે છે. ડૉક્ટરની દેખરેખ હેઠળ જ છોડાવવું જોઈએ." },
  { q: "નાલ્ટ્રેક્સોન ઈમ્પ્લાન્ટ શું છે?", a: "ચામડી નીચે મૂકાતી નાની ગોળી જે કેટલાક મહિના સુધી ધીમે ધીમે દવા છોડે છે અને દારૂ કે અફીણની તલપ રોકે છે. રોજ ગોળી લેવાની ઝંઝટ મટે છે અને શરૂઆતના નાજુક મહિનાઓમાં ફરી શરૂ થવાનું જોખમ ઘણું ઘટે છે." },
  { q: "સારવારનો ખર્ચ કેટલો થાય?", a: "OPD પરામર્શની સામાન્ય ફી હોય છે. દાખલ થવાનો ખર્ચ દિવસો અને રૂમ પ્રમાણે અગાઉથી સ્પષ્ટ જણાવવામાં આવે છે. કોઈ છૂપો ખર્ચ કે પેકેજનું દબાણ નથી." },
  { q: "સારવારની ગુપ્તતા જળવાશે?", a: "સંપૂર્ણપણે. તમારી લેખિત મંજૂરી વગર માહિતી કોઈને — સગાં, પડોશી કે નોકરીદાતાને — આપવામાં આવતી નથી." },
  { q: "એક વાર છોડ્યા પછી ફરી શરૂ થઈ ગયું છે. હવે કોઈ ફાયદો?", a: "ફરી શરૂ થવું (રીલેપ્સ) આ રોગનો ભાગ છે, નિષ્ફળતા નહીં. તે અમને જણાવે છે કે યોજનામાં શું બદલવાનું છે. ફરી આવો — અમે કોઈનો હિસાબ રાખતા નથી." },
  { q: "તમાકુ–મવો છોડાવવાની પણ સારવાર છે?", a: "હા. તલપ ઘટાડતી દવા, નિકોટિન રિપ્લેસમેન્ટ અને કાઉન્સેલિંગ સાથે તમાકુ, મવો અને બીડી છોડાવવાની સંપૂર્ણ સારવાર ઉપલબ્ધ છે — મોંમાં સફેદ ડાઘ કે કેન્સરની બીક પહેલાં જ આવો." },
];

const JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalClinic",
      "@id": `${SITE}/#deaddiction-gu`,
      name: "માનસ વ્યસનમુક્તિ કેન્દ્ર, ભાવનગર",
      alternateName: "Manas Deaddiction Centre, Bhavnagar",
      medicalSpecialty: "Psychiatry",
      inLanguage: "gu-IN",
      url: `${SITE}/gu/deaddiction`,
      telephone: "+91-94275-60101",
      foundingDate: "1989",
      address: {
        "@type": "PostalAddress",
        streetAddress: "2nd floor, Carlton Square, Near Madhavdeep Complex, Kalanala",
        addressLocality: "Bhavnagar",
        postalCode: "364001",
        addressRegion: "Gujarat",
        addressCountry: "IN",
      },
      areaServed: ["Bhavnagar", "Sihor", "Palitana", "Talaja", "Mahuva", "Botad", "Amreli", "Saurashtra", "Gujarat"],
      availableService: [
        "દારૂ છોડાવવાની સારવાર (Alcohol deaddiction)",
        "તમાકુ અને મવો છોડાવવાની સારવાર (Tobacco cessation)",
        "અફીણ અને બ્રાઉન સુગર ડિટોક્સ (Opioid detoxification)",
        "ગાંજો–ચરસ વ્યસનમુક્તિ (Cannabis deaddiction)",
        "નાલ્ટ્રેક્સોન ઈમ્પ્લાન્ટ (Naltrexone implant)",
        "વર્તન વ્યસન — મોબાઈલ, ગેમિંગ, જુગાર",
      ].map((s) => ({ "@type": "MedicalProcedure", name: s })),
      openingHoursSpecification: [{
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:00",
        closes: "20:00",
      }],
    },
    {
      "@type": "FAQPage",
      inLanguage: "gu-IN",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export default function DeaddictionGu() {
  useSEO({
    title: "ભાવનગરમાં વ્યસનમુક્તિ કેન્દ્ર · દારૂ, તમાકુ અને ડ્રગ્સ છોડાવવાની સારવાર",
    description:
      "ભાવનગરનું માનસ વ્યસનમુક્તિ કેન્દ્ર (૧૯૮૯થી) — દારૂ, તમાકુ, મવો, ગાંજો, અફીણ, બ્રાઉન સુગર, આલ્પ્રાઝોલમ અને મોબાઈલ–જુગારના વ્યસનની તબીબી દેખરેખ હેઠળ સારવાર. ડિટોક્સ, કાઉન્સેલિંગ, નાલ્ટ્રેક્સોન ઈમ્પ્લાન્ટ અને પરિવાર સાથેની થેરાપી. ડૉ. કમલેશ શાહ અને ડૉ. ધર્મિન શાહ. ફોન +૯૧ ૯૪૨૭૫ ૬૦૧૦૧.",
    image: HERO,
    jsonLd: JSONLD,
    lang: "gu",
    alternates: [
      { hreflang: "en-IN", href: `${SITE}/deaddiction` },
      { hreflang: "gu-IN", href: `${SITE}/gu/deaddiction` },
      { hreflang: "x-default", href: `${SITE}/deaddiction` },
    ],
  });
  const [open, setOpen] = useState(false);

  return (
    <div data-testid="deaddiction-gu-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <p className="eyebrow text-[#A39171]">વ્યસનમુક્તિ કેન્દ્ર · ભાવનગર</p>
          <Link
            to="/deaddiction"
            data-testid="dd-gu-switch-english"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#A39171]/40 text-[#5C524E] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
          >
            <Languages className="w-3.5 h-3.5" /> Read in English
          </Link>
        </div>
        <h1 className="font-display text-4xl md:text-6xl text-[#2D2623] leading-[1.1] tracking-tight max-w-4xl">
          વ્યસન ઇચ્છાશક્તિની કમજોરી નથી — <br />
          <span className="italic text-[#E07A5F]">એ સારવાર થઈ શકે એવો રોગ છે</span>.
        </h1>
        <p className="mt-8 text-lg md:text-xl text-[#5C524E] max-w-3xl leading-relaxed">
          દારૂ, તમાકુ–મવો, ગાંજો, અફીણ, બ્રાઉન સુગર, ઊંઘની ગોળીઓ કે મોબાઈલ–જુગારનું વ્યસન — ડૉક્ટરની
          દેખરેખ હેઠળ, માન–મર્યાદા સાચવીને છોડાવવાની સારવાર. દાખલ થઈને કે OPDમાં, બંને રીતે શક્ય.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Button data-testid="dd-gu-hero-book" onClick={() => setOpen(true)} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium">
            ગુપ્ત રીતે શરૂઆત કરો <ArrowUpRight className="ml-1 w-4 h-4" />
          </Button>
          <a href="tel:+919427560101" data-testid="dd-gu-hero-call" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] text-[#2D2623] px-8 h-14 inline-flex items-center justify-center font-medium transition-colors">
            ફોન કરો +૯૧ ૯૪૨૭૫ ૬૦૧૦૧
          </a>
        </div>
        <p className="mt-6 text-sm text-[#A39171]">
          સોમથી શનિ · સવારે ૯ થી રાત્રે ૮ · કાળાનાળા, ભાવનગર · સંપૂર્ણ ગુપ્તતા · ઈમર્જન્સી ૨૪×૭
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-24">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9] relative">
          <img src={HERO} alt="ભાવનગરના માનસ વ્યસનમુક્તિ કેન્દ્રમાં દર્દી પિતા સાથે" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/45 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 md:left-12 md:right-auto md:max-w-xl">
            <p className="eyebrow text-[#F0CF5B] mb-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">વ્યસનમુક્ત કેમ્પસ · ૫૦થી વધુ જનજાગૃતિ અભિયાન</p>
            <p className="font-display text-2xl md:text-3xl leading-snug text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
              એક ફોન — અને ઘરની શાંતિ પાછી લાવવાની શરૂઆત.
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="eyebrow text-[#A39171] mb-4">સારવારના ચાર આધારસ્તંભ</p>
            <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight leading-snug">
              ફક્ત છોડાવવું નહીં — <span className="italic text-[#E07A5F]">છૂટેલું ટકાવવું</span>.
            </h2>
            <p className="mt-6 text-[#5C524E] text-lg leading-relaxed">
              વ્યસન છોડાવવું સહેલું છે; ટકાવવું અઘરું છે. તેથી અમારી સારવાર દવા સાથે થેરાપી, પરિવાર
              અને ફોલો-અપ પર ઊભી છે.
            </p>
          </div>
          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {pillars.map((p, i) => (
              <div key={p.title} data-testid={`dd-gu-pillar-${i}`}
                className="bg-[#F4EFE6] rounded-3xl p-7 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-all">
                <span className="w-12 h-12 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center mb-5">
                  <p.icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-xl md:text-2xl text-[#2D2623] tracking-tight leading-snug">{p.title}</h3>
                <p className="text-[#5C524E] mt-2 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE TREAT */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="eyebrow text-[#A39171] mb-4">કયાં વ્યસનની સારવાર</p>
            <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight leading-snug">
              કોઈ વ્યસન એટલું મોટું નથી — <br /><span className="italic text-[#E07A5F]">કે એટલું શરમજનક પણ નથી</span>.
            </h2>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-3">
            {treats.map((tr, i) => (
              <div key={tr} data-testid={`dd-gu-treat-${i}`}
                className="bg-[#FDFBF7] rounded-2xl px-5 py-5 border border-[#A39171]/15 flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] shrink-0" />
                <span className="text-[#2D2623] font-medium text-sm md:text-base">{tr}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NALTREXONE */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
              <div className="md:col-span-7">
                <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] flex items-center justify-center mb-6">
                  <Syringe className="w-5 h-5" />
                </span>
                <p className="eyebrow text-[#E2C044] mb-3">રોજની ગોળીની ઝંઝટ વગર</p>
                <h2 className="font-display text-2xl md:text-4xl tracking-tight leading-snug">
                  નાલ્ટ્રેક્સોન ઈમ્પ્લાન્ટ — મહિનાઓ સુધી તલપ પર લગામ.
                </h2>
                <p className="text-[#FDFBF7]/80 mt-5 leading-relaxed">
                  ચામડી નીચે મૂકાતું નાનું ઈમ્પ્લાન્ટ ધીમે ધીમે દવા છોડે છે અને દારૂ કે અફીણની તલપ
                  મહિનાઓ સુધી રોકે છે. ડિટોક્સ પછીના સૌથી નાજુક સમયમાં ફરી શરૂ થવાનું જોખમ ઘણું ઘટે છે.
                </p>
              </div>
              <div className="md:col-span-5">
                <Link to="/neuromodulation" data-testid="dd-gu-link-neuromodulation" className="rounded-2xl bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 hover:bg-[#E07A5F] px-6 py-4 flex items-center justify-between transition-colors">
                  <span className="font-medium">ઈમ્પ્લાન્ટ અને અન્ય પ્રગત સારવાર</span>
                  <ArrowUpRight className="w-4 h-4 shrink-0" />
                </Link>
                <Link to="/gu/general-psychiatry" data-testid="dd-gu-link-psychiatry" className="mt-3 rounded-2xl bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 hover:bg-[#E07A5F] px-6 py-4 flex items-center justify-between transition-colors">
                  <span className="font-medium">સામાન્ય માનસિક રોગોની OPD</span>
                  <ArrowUpRight className="w-4 h-4 shrink-0" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">શું અપેક્ષા રાખવી</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-14 leading-snug">પહેલા ૯૦ દિવસ.</h2>
          <ol className="space-y-10">
            {steps.map((s, i) => (
              <li key={s.t} data-testid={`dd-gu-step-${i}`} className="grid grid-cols-12 gap-6 border-b border-[#A39171]/15 pb-10 last:border-none">
                <div className="col-span-12 md:col-span-3">
                  <span className="font-display text-4xl text-[#E07A5F]">0{i + 1}</span>
                </div>
                <div className="col-span-12 md:col-span-9">
                  <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight leading-snug">{s.t}</h3>
                  <p className="text-[#5C524E] mt-3 text-lg leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* STORIES */}
      <section className="bg-[#F4EFE6]/40 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <StoryCarousel
            category="deaddiction"
            eyebrow="દર્દીઓના શબ્દોમાં"
            heading={<>જેમને લાગતું હતું કે <span className="italic text-[#E07A5F]">હવે કંઈ થઈ શકે નહીં</span>.</>}
            note="અમારા વ્યસનમુક્તિ કાર્યક્રમમાં સારવાર લીધેલા દર્દીઓ અને પરિવારોના અનુભવ. નામ અને ઓળખ છુપાવેલી છે. (નીચેની વિગતો અંગ્રેજીમાં છે.)"
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">વારંવાર પૂછાતા પ્રશ્નો</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl leading-snug">
            પરિવારો સૌથી વધુ આ પૂછે છે.
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`ddgu-${i}`} data-testid={`dd-gu-faq-${i}`}
                className="bg-[#F4EFE6] rounded-2xl border border-[#A39171]/15 px-6 data-[state=open]:border-[#E07A5F]/35 transition-colors">
                <AccordionTrigger className="text-left font-display text-lg md:text-2xl text-[#2D2623] tracking-tight hover:no-underline py-5 leading-snug">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C524E] text-base md:text-lg leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* AREAS */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <p className="eyebrow text-[#A39171] mb-4">દર્દીઓ ક્યાંથી આવે છે</p>
          <h2 className="font-display text-2xl md:text-4xl text-[#2D2623] tracking-tight leading-snug">
            ભાવનગર અને સમગ્ર સૌરાષ્ટ્ર માટે વ્યસનમુક્તિની સારવાર.
          </h2>
          <p className="mt-6 text-[#5C524E] leading-relaxed max-w-3xl mx-auto">
            ભાવનગર, સિહોર, પાલીતાણા, તળાજા, મહુવા, ગારીયાધાર, બોટાદ, અમરેલી, રાજકોટ અને સુરતથી
            પરિવારો દારૂ, તમાકુ અને ડ્રગ્સ છોડાવવાની સારવાર માટે કાળાનાળા સ્થિત માનસ હોસ્પિટલ આવે છે.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2D2623] text-[#FDFBF7] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center relative">
          <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] flex items-center justify-center mx-auto mb-6">
            <PhoneCall className="w-5 h-5" />
          </span>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-snug">
            વ્યસનમુક્તિની શરૂઆત <span className="italic text-[#E07A5F]">એક સાચા ફોનથી</span> થાય છે.
          </h2>
          <p className="text-[#FDFBF7]/70 mt-5 max-w-xl mx-auto">
            દર્દી તૈયાર ન હોય તો પણ પરિવાર એકલા આવીને સલાહ લઈ શકે છે. બધી વાત ગુપ્ત રહેશે.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="dd-gu-cta-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium">સમય માંગો</Button>
            <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors">ફોન +૯૧ ૯૪૨૭૫ ૬૦૧૦૧</a>
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
